import { useEffect } from 'react';
import {
  ArrowRight, BarChart3, Boxes, Building2, CheckCircle2, Factory,
  Globe, RotateCcw, ShieldCheck, Store, Truck, Users, Wallet, Wrench,
} from 'lucide-react';
import { Link, useLocation } from '@tanstack/react-router';
import { MarketingShell } from './MarketingShell';
import { ProductPanel } from './PlatformLanding';
import { PlansPreview } from './PlansPreview';
import { Reveal } from './Reveal';
import { useBrand } from '../../brand/BrandProvider';
import { useCms, cmsText, cmsList } from '../../api/cms';
import { SINGLE_FALLBACKS, LIST_FALLBACKS } from '../../content/fallbacks';

/**
 * Reseller-branded landing — §5.2 / §5.3
 * Reads the host-resolved brand (useBrand, Decision #21) for name/colour/support,
 * so it renders correctly straight from the brand resolved in SessionGate — no
 * separate reseller fetch, no ITCity/Possignin leak on a reseller host. Every
 * section's copy is CMS-editable per reseller (landing.*) with brand-safe
 * fallbacks; the plans strip is LIVE catalog data (the reseller's own published
 * packages via host-aware GET /pricing).
 *
 * Chrome (header/nav/footer/theme toggle) comes from MarketingShell — the SAME
 * shell every other public page uses, so /home and /pricing can never drift.
 * Section structure mirrors the platform product landing (PlatformLanding) so a
 * reseller's page sells just as hard: hero + product panel, trust stats, the
 * complete feature grid, how-it-works, live plans, FAQ, final CTA.
 */

// Icons stay fixed in code and pair with the CMS-editable `landing.features`
// list items by position (see content/registry.ts note).
const FEATURE_ICONS = [Store, Boxes, Truck, Users, Wallet, BarChart3, Building2, ShieldCheck, Wrench, Factory, RotateCcw, Globe];

export function Landing() {
  const r = useBrand();
  const { data: cms } = useCms();
  const heroEyebrow = cmsText(cms, 'landing.hero', 'eyebrow', SINGLE_FALLBACKS['landing.hero']!.eyebrow!);
  const headlineOverride = cmsText(cms, 'landing.hero', 'headline', '');
  const subhead = cmsText(cms, 'landing.hero', 'subhead', SINGLE_FALLBACKS['landing.hero']!.subhead!);
  const ctaText = cmsText(cms, 'landing.cta', 'text', SINGLE_FALLBACKS['landing.cta']!.text!);
  const heroBadges = cmsList(cms, 'landing.heroBadges', LIST_FALLBACKS['landing.heroBadges']!);
  const trustStats = cmsList(cms, 'landing.trustStats', LIST_FALLBACKS['landing.trustStats']!);
  const featuresHeadline = cmsText(cms, 'landing.featuresIntro', 'headline', SINGLE_FALLBACKS['landing.featuresIntro']!.headline!);
  const features = cmsList(cms, 'landing.features', LIST_FALLBACKS['landing.features']!);
  const howIntro = SINGLE_FALLBACKS['landing.howItWorksIntro']!;
  const howSteps = cmsList(cms, 'landing.howItWorks', LIST_FALLBACKS['landing.howItWorks']!);
  const faqHeading = cmsText(cms, 'landing.faqIntro', 'heading', SINGLE_FALLBACKS['landing.faqIntro']!.heading!);
  const faq = cmsList(cms, 'landing.faq', LIST_FALLBACKS['landing.faq']!);
  const ctaStrip = SINGLE_FALLBACKS['landing.ctaStrip']!;

  // Scroll to the #hash target (e.g. the header's "Features" link) — on mount
  // for direct /home#features loads, and again whenever the hash changes.
  const hash = useLocation({ select: (l) => l.hash });
  useEffect(() => {
    if (!hash) return;
    const id = hash.replace(/^#/, '');
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [hash]);

  return (
    <MarketingShell>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-zinc-200/70 dark:border-zinc-800/70">
        <div className="glow-bg absolute inset-x-0 top-0 h-[36rem] -z-10 pointer-events-none" aria-hidden="true" />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-16 sm:pt-24 pb-12 sm:pb-16 text-center">
          <Reveal>
            <span className="inline-block px-3 py-1 rounded-full text-xs font-medium bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm">
              {heroEyebrow}
            </span>
          </Reveal>

          <Reveal delay={60}>
            {headlineOverride.trim() !== '' ? (
              <h1 className="mt-6 text-[2.5rem] leading-[1.04] sm:text-6xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-3xl mx-auto text-balance">
                {headlineOverride}
              </h1>
            ) : (
              <h1 className="mt-6 text-[2.5rem] leading-[1.04] sm:text-6xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 max-w-3xl mx-auto text-balance">
                Run your shop with <span className="text-brand dark:text-brand-dm">{r.name}</span>
                <br />
                <span className="text-zinc-500 dark:text-zinc-500">— built for Bangladesh.</span>
              </h1>
            )}
          </Reveal>

          <Reveal delay={120}>
            <p className="mt-5 text-lg sm:text-xl text-zinc-600 dark:text-zinc-300 max-w-2xl mx-auto leading-relaxed text-pretty">
              {subhead}
            </p>
          </Reveal>

          <Reveal delay={180}>
            <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/signup"
                className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-white bg-brand hover:bg-brand-hover shadow-sm transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
              >
                {ctaText}
                <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </Link>
              <Link
                to="/pricing"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition-colors"
              >
                See pricing
              </Link>
              <Link
                to="/login"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 bg-white/80 dark:bg-zinc-900/60 hover:bg-white dark:hover:bg-zinc-900 transition-colors"
              >
                Sign in
              </Link>
            </div>
          </Reveal>

          <Reveal delay={240}>
            <ul className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2.5 text-sm text-zinc-600 dark:text-zinc-300">
              {heroBadges.map((b, i) => (
                <li key={i} className="inline-flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-emerald-500" aria-hidden="true" /> {b.text as string}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>

        {/* Signature moment: the POS panel, wearing the reseller's name. */}
        <div className="max-w-5xl mx-auto px-4 sm:px-6 pb-14 sm:pb-20">
          <Reveal delay={120} y={28}>
            <ProductPanel title={`${r.name} · Sell`} />
          </Reveal>
        </div>
      </section>

      {/* Trust stats */}
      <section className="border-b border-zinc-200/70 dark:border-zinc-800/70 bg-white/50 dark:bg-zinc-900/30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-9 grid grid-cols-2 sm:grid-cols-4 divide-x divide-zinc-200/70 dark:divide-zinc-800/70">
          {trustStats.map((s, i) => (
            <Reveal key={i} delay={i * 60} className="text-center px-3">
              <div className="text-2xl sm:text-3xl font-semibold tabular-nums text-zinc-950 dark:text-zinc-50">{s.value as string}</div>
              <div className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{s.label as string}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Features — the COMPLETE capability grid; scroll-mt clears the sticky
          header when arriving via the nav's /home#features link. */}
      <section id="features" className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24 scroll-mt-20">
        <Reveal className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">
            Why {r.name}
          </p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
            {featuresHeadline}
          </h2>
        </Reveal>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-px rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800 bg-zinc-200/70 dark:bg-zinc-800/70">
          {features.map((f, i) => {
            const Icon = FEATURE_ICONS[i] ?? FEATURE_ICONS[FEATURE_ICONS.length - 1]!;
            return (
              <Reveal
                key={i}
                delay={(i % 3) * 60}
                className="group bg-white dark:bg-zinc-950 p-6 transition-colors hover:bg-zinc-50/80 dark:hover:bg-zinc-900/60"
              >
                <span className="w-10 h-10 grid place-items-center rounded-lg bg-brand-subtle text-brand dark:bg-brand-dm-subtle dark:text-brand-dm transition-transform duration-200 group-hover:scale-105">
                  <Icon className="w-5 h-5" aria-hidden="true" />
                </span>
                <h3 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{f.title as string}</h3>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{f.body as string}</p>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section className="border-y border-zinc-200/70 dark:border-zinc-800/70 bg-white/50 dark:bg-zinc-900/30">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          <Reveal className="text-center max-w-2xl mx-auto">
            <p className="text-xs font-semibold uppercase tracking-wider text-brand dark:text-brand-dm">
              {cmsText(cms, 'landing.howItWorksIntro', 'eyebrow', howIntro.eyebrow!)}
            </p>
            <h2 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
              {cmsText(cms, 'landing.howItWorksIntro', 'headline', howIntro.headline!)}
            </h2>
          </Reveal>
          <div className="mt-12 grid sm:grid-cols-3 gap-8 sm:gap-6 relative">
            <div className="hidden sm:block absolute left-[16%] right-[16%] top-4 h-px bg-zinc-200 dark:bg-zinc-800" aria-hidden="true" />
            {howSteps.map((s, i) => (
              <Reveal key={i} delay={i * 80} className="relative text-center sm:text-left">
                <span className="relative z-10 w-9 h-9 grid place-items-center rounded-full bg-brand text-white text-sm font-semibold tabular-nums shadow-sm dark:bg-brand-dm dark:text-zinc-950 mx-auto sm:mx-0 ring-4 ring-marketing dark:ring-marketing-dark">
                  {i + 1}
                </span>
                <h3 className="mt-4 font-semibold text-zinc-950 dark:text-zinc-50">{s.title as string}</h3>
                <p className="mt-1.5 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed max-w-xs mx-auto sm:mx-0">{s.body as string}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Live plans — the reseller's OWN published catalog (host-aware /pricing). */}
      <PlansPreview sectionKey="landing.plansPreview" />

      {/* FAQ */}
      <section className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <Reveal>
          <h2 className="text-center text-2xl sm:text-3xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
            {faqHeading}
          </h2>
        </Reveal>
        <dl className="mt-8 divide-y divide-zinc-200 dark:divide-zinc-800 rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950">
          {faq.map((row, i) => (
            <details key={i} className="group px-5 py-4 sm:px-6 [&_summary::-webkit-details-marker]:hidden" open={i === 0}>
              <summary className="flex cursor-pointer items-center justify-between gap-4 font-semibold text-zinc-950 dark:text-zinc-50">
                {row.q as string}
                <ArrowRight className="w-4 h-4 shrink-0 text-zinc-400 transition-transform duration-200 group-open:rotate-90" aria-hidden="true" />
              </summary>
              <dd className="mt-2 text-sm text-zinc-600 dark:text-zinc-300 leading-relaxed">{row.a as string}</dd>
            </details>
          ))}
        </dl>
        <p className="mt-6 text-center text-sm text-zinc-500 dark:text-zinc-400">
          Something else?{' '}
          <Link to="/contact" className="font-medium text-brand dark:text-brand-dm hover:underline">
            Contact {r.name}
          </Link>
          .
        </p>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-16 sm:py-24">
        <Reveal>
          <div className="relative rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-10 sm:p-16 text-center overflow-hidden">
            <div className="glow-bg absolute inset-x-0 top-0 h-40 pointer-events-none" aria-hidden="true" />
            <div className="relative">
              <h2 className="text-3xl sm:text-5xl font-semibold tracking-tight text-zinc-950 dark:text-zinc-50 text-balance">
                {cmsText(cms, 'landing.ctaStrip', 'headline', ctaStrip.headline!)}
              </h2>
              <p className="mt-4 text-lg text-zinc-600 dark:text-zinc-300 max-w-md mx-auto text-pretty">
                {cmsText(cms, 'landing.ctaStrip', 'body', ctaStrip.body!)}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/signup"
                  className="group w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-white bg-brand hover:bg-brand-hover shadow-sm transition-colors dark:bg-brand-dm dark:text-zinc-950 dark:hover:bg-brand-dm-hover"
                >
                  {ctaText}
                  <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
                <Link
                  to="/pricing"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg text-base font-medium text-zinc-800 dark:text-zinc-100 border border-zinc-300 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
                >
                  Compare plans
                </Link>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </MarketingShell>
  );
}
